import { useEffect, useMemo, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";

import * as echarts from "echarts";
import { Currency } from "shared/utils/Currency";
import ComboFilters from "../../ComboFilters";
import { Numbers } from "shared/utils/Numbers";
import { formatByType } from "pages/Reports/Reports";
import { If } from "shared/components/control-statements";

export default function OperationsInPeriod({
  data = [],
  isCashback,
  isBeforeNovember,
  hasUnitsReportFilters,
}) {
  const [selectedOption2, setSelectedOption2] = useState({
    name: isCashback ? "Valor" : "Pontos",
    id: "total",
  });
  const [selectedOption, setSelectedOption] = useState({
    name: isBeforeNovember ? "Custos" : "Faturamento",
    id: isBeforeNovember ? "custos" : "faturamento",
  });

  useEffect(() => {
    setSelectedOption({
      name: isBeforeNovember ? "Custos" : "Faturamento",
      id: isBeforeNovember ? "custos" : "faturamento",
    });
  }, [isBeforeNovember]);

  const _data = {
    faturamento: {
      series: ["Faturamento bruto", "Faturamento líquido"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.total?.total || 0),
        data?.map((d) => d.total_with_discounts?.total || 0),
      ],
    },
    ticket_medio: {
      series: ["Ticket médio bruto", "Ticket médio líquido"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.average_ticket?.total || 0),
        data?.map((d) => d.average_ticket_with_discounts?.total || 0),
      ],
    },
    custos: {
      series: ["Custos com disparos", "Descontos totais"],
      value_type: "currency",
      colors: ["#FF9F68", "#5AE8F4"],
      data: [
        data?.map((d) => d.send_cost?.total || 0),
        data?.map((d) => d.discounts?.total || 0),
      ],
    },
    expired: {
      series: ["Expirado"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.expired?.[selectedOption2.id] || 0)],
    },
    received: {
      series: ["Recebido"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.received?.[selectedOption2.id] || 0)],
    },
    used: {
      series: ["Usado"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.used?.[selectedOption2.id] || 0)],
    },
    chargeback: {
      series: ["Estornado"],
      value_type: isCashback ? "currency" : "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.chargeback?.[selectedOption2.id] || 0)],
    },
    use_ratio: {
      series: ["Taxa de uso"],
      value_type: "percentage",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.use_ratio?.total || 0)],
    },
    expired_ratio: {
      series: ["Taxa de expiração"],
      value_type: "percentage",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.expired_ratio?.total || 0)],
    },
    average_use_time: {
      series: ["Tempo médio para uso"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.average_use_time?.total || 0)],
    },
    roi: {
      series: ["ROI do cupom"],
      value_type: "int",
      colors: ["#f288a4"],
      data: [data.map((d) => d?.roi?.total || 0)],
    },
  };

  const totalData = [];

  const getDataByIndex = (index) => {
    return data[index];
  };

  const memoData = useMemo(() => data, [JSON.stringify(data)]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const chartDOM = document.getElementById("operations-in-period-chart");
      const chart = echarts.init(chartDOM, null, {
        renderer: "svg",
      });
      const selectedOptionId = selectedOption.id;

      const options = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "none",
          },
          backgroundColor: "#2B2B2B",
          borderRadius: 8,
          padding: [8, 8],
          borderWidth: 0,
          textStyle: {
            color: "#FFF",
            fontSize: 10,
          },
          formatter: (params) => {
            const index = params[0].dataIndex;
            const item = getDataByIndex(index);

            if (
              !["faturamento", "ticket_medio", "custos"].includes(
                selectedOption.id
              )
            )
              return "";

            return `
                                        ${
                                          selectedOption.id === "faturamento"
                                            ? `
                    <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Faturamento bruto:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.total?.total)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Faturamento líquido:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(
                          +item.total_with_discounts?.total
                        )} 
                      </span>  
                    </div>
                `
                                            : ""
                                        }

                   ${
                     selectedOption.id === "ticket_medio"
                       ? `
                    <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Ticket médio bruto:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.average_ticket?.total)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Ticket médio líquido:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(
                          +item.average_ticket_with_discounts?.total
                        )} 
                      </span>  
                    </div>
                `
                       : ""
                   }

                            ${
                              selectedOption.id === "custos"
                                ? `
                    <div style="background-color: rgba(97, 97, 255, 0.15);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#8a8af5;font-weight:700">
                        Custo total:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.cost?.total)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(255 159 104 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#FF9F68;font-weight:700">
                        Custo com disparos:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.send_cost?.total)} 
                      </span>  
                    </div>

                    <div style="background-color: rgb(90 232 244 / 10%);padding:8px;border-radius:8px;color:#A0A0A0;margin-bottom:5px;">
                      <span style="color:#5AE8F4;font-weight:700">
                        Custo com descontos:
                      </span>  
                      <span style="color:#fff;margin-left:4px;">
                        ${Currency.formatCurrency(+item.discounts?.total)} 
                      </span>  
                    </div>
                `
                                : ""
                            }
            `;
          },
        },
        grid: {
          left: 0,
          right: 10,
          top: 30,
          bottom: 0,
          containLabel: true,
        },
        xAxis: [
          {
            type: "category",
            data: data.map((d) => d.date),
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            axisLine: {
              lineStyle: {
                color: "rgba(217, 217, 217, 0.5)",
              },
            },
            splitLine: {
              lineStyle: {
                color: "rgba(51, 51, 51, 0.55)",
                width: 1,
                type: "solid",
              },
            },
            axisLabel: {
              formatter: (value) =>
                ["total"].includes(selectedOption2.id) &&
                [
                  "expired",
                  "received",
                  "used",
                  "chargeback",
                  "faturamento",
                  "ticket_medio",
                  "custos",
                ].includes(selectedOption.id)
                  ? isCashback
                    ? Currency.formatCurrency(value)
                    : Numbers.formatInt(value)
                  : Numbers.formatInt(value),
              color: "rgba(217, 217, 217, 0.5)",
            },
          },
        ],
        series: _data[selectedOptionId].series.map((name, sid, series) => {
          const stacked = !["faturamento", "ticket_medio"].includes(
            selectedOptionId
          );

          return {
            name,
            type: "bar",
            stack: stacked ? "total" : undefined,
            barWidth: stacked ? "80%" : "30%",
            barGap: "5%",
            data: _data[selectedOptionId].data[sid].map((d, did) =>
              totalData[did] <= 0 ? 0 : d
            ),
            label: {
              show: stacked ? sid == series.length - 1 : false,
              position: "top",
              lineHeight: 20,
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: 10,
              fontWeight: "semibold",
              formatter: (params) => {
                if (stacked) {
                  const index = params.dataIndex;
                  const total = _data[selectedOptionId].data.reduce(
                    (acc, curr) => acc + curr[index],
                    0
                  );
                  return total > 0
                    ? formatByType(
                        total,
                        selectedOption2.id === "total"
                          ? _data[selectedOptionId].value_type
                          : "int"
                      )
                    : "";
                } else {
                  const value = params.value;
                  return value > 0
                    ? formatByType(value, _data[selectedOptionId].value_type)
                    : "";
                }
              },
            },
            itemStyle: {
              color: _data[selectedOptionId].colors[sid],
            },
          };
        }),
      };

      options && chart.setOption(options);

      const ro = new ResizeObserver(() => {
        chart.resize();
      });
      ro.observe(chartDOM);

      return () => {
        ro.unobserve(chartDOM);
        ro.disconnect();
        chart.dispose();
      };
    }
  }, [memoData, selectedOption, selectedOption2]);

  const rawData = _data?.[selectedOption.id]?.data[0] || [];
  const rawData2 = _data?.[selectedOption.id]?.data[1] || [];

  const normalizedData = (data) => {
    data.map((arr) => {
      if (Array.isArray(arr))
        return arr.map((d) => (typeof d === "object" ? d.value : d));

      return arr;
    });

    return data;
  };

  return (
    <div className={styles.box}>
      <div className="d-flex align-center justify-between mb-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Indicadores no período</p>
        </div>
        <div className="d-flex gap-5">
          <If
            cond={["received", "used", "expired", "chargeback"].includes(
              selectedOption.id
            )}
          >
            <ComboFilters
              options={[
                { name: isCashback ? "Valor" : "Pontos", id: "total" },
                { name: "Operações", id: "count" },
              ]}
              onSelect={(option) => setSelectedOption2(option)}
              selectedOption={selectedOption2 || { name: "Valor", id: "total" }}
            />
          </If>
          <ComboFilters
            options={[
              ...(isBeforeNovember
                ? []
                : [
                    { name: "Faturamento", id: "faturamento" },
                    { name: "Ticket médio", id: "ticket_medio" },
                  ]),
              { name: "Custos", id: "custos" },
              { name: "Taxa de uso", id: "use_ratio" },
              ...(hasUnitsReportFilters
                ? []
                : [{ name: "Taxa de expiração", id: "expired_ratio" }]),
              { name: "Tempo médio para uso", id: "average_use_time" },
              { name: "ROI do cupom", id: "roi" },
              { name: "Concessões", id: "received" },
              { name: "Consumo", id: "used" },
              ...(hasUnitsReportFilters
                ? []
                : [
                    { name: "Expirações", id: "expired" },
                    { name: "Estornos", id: "chargeback" },
                  ]),
            ]}
            onSelect={(option) => setSelectedOption(option)}
            selectedOption={selectedOption}
          />
        </div>
      </div>

      <div className={styles.chart}>
        {["faturamento", "ticket_medio", "custos"].includes(
          selectedOption.id
        ) ? (
          normalizedData(rawData)?.every((s) => s === 0 || s === null) &&
          normalizedData(rawData2)?.every((s) => s === 0 || s === null)
        ) : normalizedData(rawData)?.every((s) => s === 0 || s === null) ? (
          <div className={styles.emptyChart} style={{ height: 350 }}>
            <span className="text-grey">Não há dados neste período</span>
          </div>
        ) : null}

        <div
          id="operations-in-period-chart"
          style={{ width: "100%", height: 350 }}
        />
      </div>
    </div>
  );
}
